import { Need } from '@lmc/ts-common'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useTranslation } from 'react-i18next'

// Extends the base Need type with modsCategory from the v6 API
type NeedItem = Need & {
  modsCategory?: string
}

type NeedsPagination = {
  totalItems: number
  currentPage: number
  itemsPerPage: number
}

// The v6 API uses { data, pagination } (not the legacy { result, count } shape)
type NeedsResponse = {
  data: NeedItem[]
  pagination: NeedsPagination
}

type UseNeedsParams = {
  page?: number
  limit?: number
  sortBy?: 'createdOn' | 'name'
  sortOrder?: 'asc' | 'desc'
}

type UseNeedsReturn = {
  needs: NeedItem[]
  pagination?: NeedsPagination
  isLoading: boolean
  refetch: () => void
}

// Query key constants — use these everywhere for consistent cache invalidation
export const NEEDS_QUERY_KEYS = {
  list: 'needs',
  options: 'needOptions',
  suggestions: 'needSuggestions',
} as const

const DEFAULT_PARAMS: Required<UseNeedsParams> = {
  page: 1,
  limit: 10,
  sortBy: 'createdOn',
  sortOrder: 'desc',
}

const getNeedsUrl = (params: UseNeedsParams) => {
  const { page, limit, sortBy, sortOrder } = { ...DEFAULT_PARAMS, ...params }
  return `/v6/needs?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
}

const fetchNeeds = async (params: UseNeedsParams): Promise<NeedsResponse> => {
  const url = getNeedsUrl(params)
  return (await getJSON({ url })) as NeedsResponse
}

const useNeeds = (params: UseNeedsParams = {}): UseNeedsReturn => {
  const { page, limit, sortBy, sortOrder } = { ...DEFAULT_PARAMS, ...params }

  const { data, isLoading, refetch } = useQuery<NeedsResponse>(
    [NEEDS_QUERY_KEYS.list, page, limit, sortBy, sortOrder],
    () => fetchNeeds(params),
    {
      retry: false,
      retryOnMount: false,
    },
  )

  return {
    needs: data?.data ?? [],
    pagination: data?.pagination,
    isLoading,
    refetch,
  }
}

export const fetchAllNeeds = async (): Promise<NeedItem[]> => {
  try {
    const result = (await getJSON({ url: '/v6/needs/options' })) as { data: NeedItem[] }
    return result.data ?? []
  } catch {
    return []
  }
}

export const fetchNeedSuggestions = async (): Promise<NeedItem[]> => {
  try {
    const result = (await getJSON({ url: '/v6/needs/suggestions' })) as { data: NeedItem[] }
    return result.data ?? []
  } catch {
    return []
  }
}

export const useNeedCreate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation(
    async (payload: { name: string; modsCategory?: string }) => {
      const result = await postJSON({
        url: '/v6/needs',
        json: payload,
      })
      return result.data as NeedItem
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.needAdded') })
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.list])
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.options])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needAddFailed'),
        })
      },
    }
  )

  return {
    createNeed: mutation.mutateAsync,
    isCreating: mutation.isLoading,
  }
}

export const useNeedUpdate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation(
    async ({ id, ...payload }: { id: string; name: string; modsCategory?: string }) => {
      const result = await postJSON({
        url: `/v6/needs/${id}`,
        method: 'PUT',
        json: { id, ...payload },
      })
      return result.data as NeedItem
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.changesSaved') })
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.list])
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.options])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needUpdateFailed'),
        })
      },
    }
  )

  return {
    updateNeed: mutation.mutateAsync,
    isUpdating: mutation.isLoading,
  }
}

export const useNeedDelete = (onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const mutation = useMutation(
    async (id: string) => {
      await postJSON({
        url: `/v6/needs/${id}`,
        method: 'DELETE',
        responseType: 'text',
      })
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.needDeleted') })
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.list])
        queryClient.invalidateQueries([NEEDS_QUERY_KEYS.options])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needDeleteFailed'),
        })
      },
    }
  )

  return {
    deleteNeed: mutation.mutateAsync,
    isDeleting: mutation.isLoading,
  }
}

export default useNeeds
export type { NeedItem, NeedsPagination, NeedsResponse, UseNeedsParams }
